import { createFileRoute } from "@tanstack/react-router";
import { MarkLast, ReadingPage, readingBody, readingEyebrow, readingH1, readingH2, readingLink, readingSection, readingUpdated } from "@/components/public/reading-page";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import {
  CONTACT_EMAIL,
  SELLER_COUNTRY_AR,
  SELLER_COUNTRY_EN,
  SELLER_LEGAL_NAME_AR,
  SELLER_LEGAL_NAME_EN,
} from "@/lib/legal/seller";

export const Route = createFileRoute("/_public/privacy")({
  head: () => ({
    meta: [
      { title: "سياسة الخصوصية — د. سارة النحّاس" },
      { name: "description", content: "كيف نجمع بياناتك ونستخدمها ونحميها في منصّة د. سارة النحّاس." },
      { property: "og:title", content: "سياسة الخصوصية — د. سارة النحّاس" },
      { property: "og:description", content: "كيف نجمع بياناتك ونستخدمها ونحميها." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      ...ogImageMeta(),
      ...canonicalMeta("/privacy"),
    ],
    links: [canonicalLink("/privacy")],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: PrivacyPage,
});

const sections: { title: string; body: string }[] = [
  {
    title: "البيانات التي نجمعها",
    body: "نجمع فقط ما نحتاجه لتقديم الخدمة: الاسم، البريد الإلكتروني، رقم التواصل، بيانات الحجز ونموذج الاستشارة، إجابات اختبار البوصلة ونموذج التفضيلات، وإيصالات الدفع التي ترفعها. لا نجمع بيانات حسّاسة إضافية دون إذنك.",
  },
  {
    title: "كيف نستخدم بياناتك",
    body: "نستخدم بياناتك لإدارة الحجوزات، إعداد التقارير وإرسالها، مراجعة المدفوعات، تحسين الخدمة، والتواصل معك حول جلساتك. لا نبيع بياناتك ولا نشاركها مع أي جهة تسويقية.",
  },
  {
    title: "إجابات نموذج التفضيلات",
    body: "عند شرائك البوصلة الذكية نطلب منك: التخصصات التي تفكّر بها، بلد الدراسة المفضّل، الإطار المالي للدراسة، توقعات أهلك، أهدافك، ومخاوفك. تبقى هذه الإجابات مرئية لك ولفريق الإدارة فقط، وتُستخدم حصراً لإعداد تقريرك.",
  },
  {
    title: "بيانات الأطفال",
    body: "الأهل هم من يقدّمون المعلومات عن طفلهم في نموذج الاستشارة ونماذج متابعة الحالة. هذه المعلومات يراها فريق د. سارة فقط، ولا تُنشر أبداً ولا تُستخدم في أي محتوى عام. يمكن للأهل طلب نسختها أو تصحيحها أو حذفها في أي وقت.",
  },
  {
    title: "الذكاء الاصطناعي",
    body: "نستخدم نموذج ذكاء اصطناعي من OpenAI لصياغة مسودة تقرير البوصلة الذكية. نرسل له نتائج الاختبار وإجابات النموذج فقط — لا الاسم ولا البريد ولا الرقم. يراجع فريق د. سارة كل تقرير قبل إرساله.",
  },
  {
    title: "مين يستلم البيانات",
    body: "مزوّد استضافة سحابية موثوق (الاستضافة وقاعدة البيانات والتخزين وتسجيل الدخول)؛ OpenAI (مسودة تقرير البوصلة الذكية فقط)؛ PayPal (عند الدفع عبره)؛ مزوّد خدمة البريد الإلكتروني (رسائل تأكيد الحساب وتسجيل الدخول)؛ Google (عند تسجيل الدخول بحساب Google)؛ Google Analytics بالمعرّف G-HEK40SRGNN (إحصاءات زيارة الصفحات، حسب موافقتك)؛ SmartStack DocEngine (تنسيق ملف برنامج ما بعد الاستشارة دون تعديل نصّه)؛ وواتساب عند تواصلك معنا عبره. وقد نشارك بيانات مع الجهات الرسمية فقط إذا فرض القانون ذلك.",
  },
  {
    title: "مزوّدو الخدمة",
    body: "نتعامل فقط مع مزوّدين يلتزمون بحماية البيانات، ونشاركهم ما يلزم لتشغيل الخدمة فقط.",
  },
  {
    title: "مدة الاحتفاظ",
    body: "نحتفظ ببياناتك طالما حسابك فعّال أو طالما نحتاجها لتقديم الخدمة التي طلبتها. عند حذف الحساب نحذف البيانات أو نجعلها مجهولة الهوية، إلا ما يفرض القانون الاحتفاظ به (مثل سجلات الدفع)، فنحتفظ به للمدة المطلوبة فقط.",
  },
  {
    title: "الحفظ والحماية",
    body: "نُخزّن البيانات على بنية سحابية آمنة مع تشفير وصلاحيات مُحكمة. الملاحظات السريرية الخاصّة بالطفل محفوظة برؤية الطاقم فقط.",
  },
  {
    title: "ملفات تعريف الارتباط والموافقة",
    body: "نستخدم ملفات ضرورية لتسجيل الدخول وحماية الموقع. نستخدم Google Analytics لفهم استخدام الصفحات: في الاتحاد الأوروبي والمملكة المتحدة وسويسرا لا يعمل قبل موافقتك، وفي باقي الدول يعمل ما لم ترفضه. يمكنك تغيير اختيارك في أي وقت من رابط «إعدادات الخصوصية» أسفل كل صفحة.",
  },
  {
    title: "حقوقك",
    body: "يحقّ لك طلب نسخة من بياناتك، تصحيحها، أو حذفها في أي وقت. يمكنك أيضاً سحب موافقتك على المعالجة عبر التواصل معنا.",
  },
  {
    title: "حذف الحساب",
    body: "لحذف حسابك وكل بياناتك، راسلنا من البريد المسجَّل في الحساب أو على واتساب. نؤكّد الطلب ونحذف الحساب، ونبلغك بما نحتفظ به قانونياً (مثل سجلات الدفع) إن وُجد.",
  },
  {
    title: "التواصل معنا",
    body: "لأي استفسار حول الخصوصية، راسلنا عبر البريد أو صفحة «تواصل معنا»، ونردّ خلال 48 ساعة عمل.",
  },
];

const enSections: { title: string; body: string }[] = [
  {
    title: "Who we are",
    body: `This website and its paid products are operated by ${SELLER_LEGAL_NAME_EN}, a sole proprietor based in the ${SELLER_COUNTRY_EN}, acting as the data controller for the personal data described here. Contact: ${CONTACT_EMAIL}`,
  },
  {
    title: "Data we collect",
    body: "We collect only what we need to provide the service: your name, email address, contact number, booking and consultation-intake data, Bousala test and preferences-form answers, and payment receipts you upload. We do not collect additional sensitive data without your permission.",
  },
  {
    title: "How we use your data",
    body: "We use your data to manage bookings, prepare and send reports, review payments, improve the service, and contact you about your sessions. The legal bases are performance of our contract with you, our legitimate interest in running and securing the service, and your consent where we ask for it. We do not sell your data and do not share it with marketing parties.",
  },
  {
    title: "Preferences form answers",
    body: "When you purchase Smart Bousala we ask about the majors you are considering, your preferred country of study, your financial context, your family's expectations, your goals, and your concerns. These answers are visible only to you and our team, and are used solely to prepare your report.",
  },
  {
    title: "Children's data",
    body: "Parents provide information about their child in the consultation intake and case forms. It is visible only to Dr. Sara's team and is never published or used in public content. Parents can ask for a copy, a correction or deletion at any time.",
  },
  {
    title: "Artificial intelligence",
    body: "We use an OpenAI model to draft the Smart Bousala report. We send it only your assessment results and form answers — never your name, email, or phone number. Dr. Sara's team reviews every report before it is sent to you.",
  },
  {
    title: "Who receives data",
    body: "A trusted cloud hosting provider (hosting, database, file storage and sign-in); OpenAI (Smart Bousala drafts only); PayPal (when you pay that way); our email service provider (account and sign-in emails); Google (when you sign in with Google); Google Analytics, ID G-HEK40SRGNN (page-usage statistics, subject to your consent); SmartStack DocEngine (layout of post-consultation program files, without changing their text); and WhatsApp when you contact us there. We share data with authorities only where the law requires it.",
  },
  {
    title: "Service providers",
    body: "We only work with providers that commit to protecting data, and share only what is needed to run the service.",
  },
  {
    title: "Retention",
    body: "We keep your data while your account is active or while we need it to provide a service you asked for. When you delete your account we delete or anonymise your data, except what the law requires us to keep (such as payment records), which we keep only for the required period.",
  },
  {
    title: "Storage and security",
    body: "Data is stored on a secure cloud infrastructure with encryption and strict access control; clinical notes about a child are visible to clinical staff only.",
  },
  {
    title: "Cookies and consent",
    body: "We use essential cookies to keep you signed in and to secure the service. We use Google Analytics to understand how pages are used: in the EEA, UK and Switzerland it stays off until you accept; elsewhere it runs unless you decline. You can change your choice at any time from the “Privacy settings” link at the bottom of every page.",
  },
  {
    title: "Your rights",
    body: "You may request a copy of your data, its correction, or its deletion at any time, object to or restrict processing, ask for portability, and withdraw consent. Write to us and we will respond within 48 business hours.",
  },
  {
    title: "Deleting your account",
    body: "To delete your account and all your data, email us from the address on the account or message us on WhatsApp. We confirm the request, delete the account, and tell you what, if anything, we must keep by law (such as payment records).",
  },
  {
    title: "Contact",
    body: `For any privacy question, write to ${CONTACT_EMAIL} or use the contact page.`,
  },
];

function EnBlock({ title, body }: { title: string; body: string }) {
  return (
    <section className={"reveal " + readingSection}>
      <h2 className="font-display text-[23px] font-extrabold leading-snug text-text-primary">{title}</h2>
      <p className={readingBody}>{body}</p>
    </section>
  );
}

function PrivacyPage() {
  return (
    <ReadingPage
      header={
        <>
            <p className={readingEyebrow}>الخصوصية</p>
            <h1 className={readingH1}><MarkLast text="سياسة الخصوصية" /></h1>
            <p className={readingUpdated}>
              آخر تحديث: تشرين الأول 2026
            </p>
        </>
      }
    >
      <p className="mt-4 leading-[1.85] text-brown-500">
        تُدار هذه المنصّة من قبل {SELLER_LEGAL_NAME_AR}، مؤسسة فردية مقرّها {SELLER_COUNTRY_AR}،
        وهي الجهة المسؤولة عن معالجة بياناتك. للتواصل: {CONTACT_EMAIL}
      </p>
      <div className="mt-10 space-y-8">
        {sections.map((s, i) => (
          <section key={s.title} className={(i < 2 ? "" : "reveal ") + readingSection}>
            <h2 className="font-display text-[23px] font-extrabold leading-snug text-text-primary">{s.title}</h2>
            <p className={readingBody}>{s.body}</p>
          </section>
        ))}
        <section id="google-user-data" className={"reveal " + readingSection}>
          <h2 className="font-display text-[23px] font-extrabold leading-snug text-text-primary">
            بيانات حساب Google / Google user data
          </h2>
          <p className={readingBody}>
            عند اختيارك تسجيل الدخول عبر Google، نطلب فقط بيانات ملفك الشخصي الأساسية: الاسم، صورة
            الملف الشخصي، عنوان البريد الإلكتروني، ومعرّف حساب Google. نستخدمها حصراً لإنشاء حسابك،
            التعرّف عليك عند كل تسجيل دخول، ربط حجوزاتك وتقاريرك بحسابك، وإرسال رسائل الحجز
            والتذكير. لا نطّلع على Gmail أو تقويم Google أو Google Drive أو أي خدمة أخرى من Google.
            لا نبيع بياناتك ولا ننقلها إلى أطراف ثالثة إلا بالقدر اللازم لتشغيل الخدمة (مثل مزوّدي
            الاستضافة والبريد). يمكنك سحب الإذن في أي وقت من إعدادات حساب Google، أو مراسلتنا لحذف
            حسابك وبياناتك.
          </p>
          <div dir="ltr" lang="en" className="mt-5 border-t border-hairline-default pt-5 text-start">
            <p className="text-[14px] leading-relaxed text-brown-500">
              When you choose to sign in with Google, we request only your basic Google profile:
              your name, profile picture, email address, and Google account ID. We use this data
              solely to create your account, identify you at each sign-in, link your bookings and
              reports to your account, and send booking confirmations and reminders. We do not
              access Gmail, Google Calendar, Google Drive, or any other Google service. We do not
              sell your data and do not transfer it to third parties except as needed to provide the
              service (for example, hosting and email delivery providers). You can revoke access at
              any time from your Google Account settings (myaccount.google.com → Security →
              Third-party apps), or contact us to delete your account and data.
            </p>
          </div>
        </section>
      </div>

      <div dir="ltr" lang="en" className="mt-16 border-t border-hairline-default pt-10 text-start">
        <h2 className="font-display text-[30px] font-extrabold text-text-primary">Privacy Notice</h2>
        <p className={readingUpdated}>Last updated: October 2026</p>
        <div className="mt-8 space-y-8">
          {enSections.map((s) => (
            <EnBlock key={s.title} title={s.title} body={s.body} />
          ))}
        </div>
      </div>
    </ReadingPage>
  );
}